import { useState, useMemo, useCallback, useEffect } from 'react';
import parse from 'html-react-parser';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import Card from '../card/card';
import CtaButton from '../ctaButton/ctaButton';
import styles from './featureOverview.module.scss';

export default function FeatureOverview({ headline, cardsWithImage }) {
  const { width } = useWindowDimensions();
  const [activeTitle, setActiveTitle] = useState(
    cardsWithImage[0]?.title || ''
  );
  const [imageFadeKey, setImageFadeKey] = useState(0);

  const isDesktop = width >= 1024;

  const activeCard = useMemo(
    () => cardsWithImage.find((card) => card.title === activeTitle),
    [cardsWithImage, activeTitle]
  );

  useEffect(() => {
    setImageFadeKey((k) => k + 1);
  }, [activeCard?.media]);

  const splitCards = useMemo(() => {
    return {
      left: cardsWithImage.filter((_, i) => i % 2 !== 0),
      right: cardsWithImage.filter((_, i) => i % 2 === 0),
    };
  }, [cardsWithImage]);

  const handleActivate = useCallback((title) => {
    setActiveTitle(title);
  }, []);

  const renderCardList = useCallback(
    (cards) =>
      cards.map((card) => {
        const isActive = activeTitle === card.title;
        const cardClasses = [
          styles.cardItem,
          !isDesktop && styles.show,
          isDesktop && isActive && styles.hovered,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={card.title}
            onMouseEnter={() => isDesktop && handleActivate(card.title)}
            className={styles.cardListWrapper}
          >
            <Card
              {...card}
              className={cardClasses}
              cardContainerClassName={styles.cardContainer}
              headlineClassName={styles.cardHeadLine}
              descriptionClassName={styles.cardDescription}
              ctaButtonClassName={styles.cardButton}
            />
          </div>
        );
      }),
    [activeTitle, handleActivate, isDesktop]
  );

  const renderImage = (media) =>
    media && (
      <div className={styles.imageContainer}>
        <img
          key={imageFadeKey}
          src={media.fields.file.url}
          alt={media.fields.title}
          className={styles.show}
        />
      </div>
    );

  const renderMaxSize = (
    <div className={styles.cardwithImageContainer}>
      <div className={styles.card}>{renderCardList(splitCards.left)}</div>
      {renderImage(activeCard?.media)}
      <div className={styles.card}>{renderCardList(splitCards.right)}</div>
    </div>
  );

  const renderMiniSize = (
    <>
      <div className={styles.cardButton}>
        {cardsWithImage.map((card) => (
          <CtaButton
            key={card.title}
            label={card.title}
            onClick={() => handleActivate(card.title)}
            variant={card.title === activeTitle ? 'solid' : ''}
          />
        ))}
      </div>
      {activeCard && (
        <div className={styles.cardwithImageContainer}>
          {renderCardList([activeCard])}
          {renderImage(activeCard.media)}
        </div>
      )}
    </>
  );

  return (
    <div className={styles.featureOverviewWrapper}>
      <div className={styles.featureOverviewContainer}>
        <h1 className={styles.headline}>{parse(headline)}</h1>
        {isDesktop ? renderMaxSize : renderMiniSize}
      </div>
    </div>
  );
}
